import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolvePromoImage } from '../../images/promoAssets';
import { Promotion } from '../../services/PromotionsService';

export type FeaturedEvent = {
  id: number;
  fullTitle?: string;
  title: string;
  allDay?: boolean;
  start: { toFormat: (fmt: string) => string };
  href?: string;
  urlactive?: boolean;
  description?: string;
};

type Slide = {
  key: string;
  eyebrow?: string;
  title: string;
  when?: string;
  text?: string;
  image?: string;
  buttonLabel: string;
  /** Where the whole card goes; undefined = card is not clickable. */
  link?: string;
};

const ROTATE_MS = 8000;

function oneLine(s?: string | null) {
  return (s || '').replace(/\s+/g, ' ').trim();
}

export function buildSlides(promotions: Promotion[], fe?: FeaturedEvent): Slide[] {
  const slides: Slide[] = promotions.map((p) => ({
    key: `promo-${p.id}`,
    title: p.headline,
    text: oneLine(p.body),
    image: resolvePromoImage(p.imageUrl, p.imageKey),
    buttonLabel: p.buttonLabel || 'Learn more',
    link: (p.linkUrl || '').trim() || undefined,
  }));
  if (fe) {
    slides.push({
      key: `event-${fe.id}`,
      eyebrow: 'Featured Event',
      title: fe.fullTitle || fe.title,
      when: fe.allDay
        ? fe.start.toFormat('cccc, LLLL d')
        : `${fe.start.toFormat('cccc, LLLL d')} at ${fe.start.toFormat('h:mm a')}`,
      text: oneLine(fe.description),
      buttonLabel: 'Details',
      link: fe.href && fe.urlactive !== false ? fe.href : '/events',
    });
  }
  return slides;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export default function HomeSpotlight({ promotions, featuredEvent }: { promotions: Promotion[]; featuredEvent?: FeaturedEvent }) {
  const slides = useMemo(() => buildSlides(promotions, featuredEvent), [promotions, featuredEvent]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [slides.length, index]);

  useEffect(() => {
    if (slides.length < 2 || paused || reduced) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => window.clearInterval(t);
  }, [slides.length, paused, reduced]);

  if (slides.length === 0) return null;
  const s = slides[Math.min(index, slides.length - 1)];

  const content = (
    <>
      {s.image && <img src={s.image} alt="" className="spotlight-img" />}
      <div className="spotlight-body">
        {s.eyebrow && <div className="spotlight-eyebrow">{s.eyebrow}</div>}
        <h3 className="spotlight-title">{s.title}</h3>
        {s.when && <div className="spotlight-when">{s.when}</div>}
        {s.text && <p className="spotlight-desc">{s.text}</p>}
      </div>
    </>
  );

  let main: ReactNode;
  if (!s.link) main = <div className="spotlight-main">{content}</div>;
  else if (/^https?:/i.test(s.link)) main = <a href={s.link} className="spotlight-main spotlight-link">{content}</a>;
  else main = <Link to={s.link} className="spotlight-main spotlight-link">{content}</Link>;

  return (
    <div
      className="spotlight-card mb-3"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      aria-roledescription={slides.length > 1 ? 'carousel' : undefined}
      aria-live={paused || reduced ? 'polite' : 'off'}
    >
      {main}
      {slides.length > 1 && (
        <div className="spotlight-dots" role="tablist" aria-label="Choose highlight">
          {slides.map((x, i) => (
            <button
              key={x.key}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Show ${x.title}`}
              className={i === index ? 'spotlight-dot active' : 'spotlight-dot'}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
